import Microgame from '../engine/Microgame.js';

export default class ConnectGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Connecte !`, en: `Connect!`, es: `¡Conecta!` },
            controls: { fr: `Tire un trait entre les points de même couleur`, en: `Draw a line between dots of the same color`, es: `Dibuja una línea entre puntos del mismo color` },
            objective: { fr: `Relie tous les points avant l'explosion`, en: `Connect all same-colored dots before time ends`, es: `Conecta todos los puntos del mismo color antes del tiempo` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="19" r="3"/><path d="m8.5 16.5 7-9"/></svg>`,
            actions: [
                { type: 'wait', duration: 260 },
                { type: 'swipe', fromX: 16, fromY: 50, toX: 84, toY: 50, duration: 750 },
                { type: 'wait', duration: 600 },
            ],
            loopDuration: 2600
        };
    }
    constructor(engine) {
        super(engine);
        this.startPoint = null;
        this.endPoint = null;
        this.canvas = null;
        this.ctx = null;
        this.isDrawing = false;
        this.path = [];
        this.obstacles = [];

        // Progression ralentie : on amortit la vitesse pour ne pas que ce soit "impossible" à x4,
        // mais on garde un vrai défi : 0.4 est choisi pour arriver à ~1.6s en x4 (en partant de 4s)
        // Calcul: 4000 * (4 / (1 + (4-1)*0.4)) = 4000 * (4 / 2.2) = 4000 * 1.81 = 7272 (divisé par 4 = 1818ms -> ~1.8s) 
        // Pour arriver vraiment à 1.6s -> temps après div par speedMult = 1.6s -> temps "durée donnée au moteur" = 6.4s
        // Base = 4000. 4000 * (4 / X) = 6400 -> 16000 / X = 6400 -> X = 2.5
        // 1 + 3 * amort = 2.5 -> 3 * amort = 1.5 -> amort = 0.5
        const speedMult = engine.speedMultiplier || 1.0;
        const adjustedSpeed = 1 + (speedMult - 1) * 0.5;

        // Base time : 4000ms. En x4 (adjusted 2.5), on aura environ ~1.6 secondes.
        this.duration = 4000 * (speedMult / adjustedSpeed);
    }

    setup() {
        this.engine.ui.instruction.textContent = "CONNECT!";

        // Canvas for drawing
        this.canvas = document.createElement('canvas');
        this.canvas.width = this.container.clientWidth;
        this.canvas.height = this.container.clientHeight;
        this.canvas.style.position = 'absolute';
        this.canvas.style.top = '0';
        this.canvas.style.left = '0';
        this.container.appendChild(this.canvas);
        this.ctx = this.canvas.getContext('2d');

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);

        this.startRound();
    }

    startRound() {
        const w = this.canvas.width;
        const h = this.canvas.height;
        this.path = [];
        this.obstacles = [];

        // Randomize Start/End
        const padding = 50;
        // Option 1: Left to Right
        // Option 2: Top to Bottom
        // Option 3: Diagonal

        // Simple Randomization: Start on Left/Top, End on Right/Bottom
        if (this.isPlayback) {
            this.startPoint = { x: padding, y: h * 0.5, r: 20 };
            this.endPoint = { x: w - padding, y: h * 0.5, r: 20 };
        } else if (Math.random() < 0.5) {
            // Horizontal-ish
            this.startPoint = { x: padding, y: padding + Math.random() * (h - 2 * padding), r: 20 };
            this.endPoint = { x: w - padding, y: padding + Math.random() * (h - 2 * padding), r: 20 };
        } else {
            // Vertical-ish
            this.startPoint = { x: padding + Math.random() * (w - 2 * padding), y: padding, r: 20 };
            this.endPoint = { x: padding + Math.random() * (w - 2 * padding), y: h - padding, r: 20 };
        }

        // Random Obstacles (3 to 5)
        const numObstacles = this.isPlayback ? 0 : (3 + Math.floor(Math.random() * 3));
        const safeDist = 60; // Distance from start/end to avoid cheap deaths

        for (let i = 0; i < numObstacles; i++) {
            let valid = false;
            let obs = {};
            let attempts = 0;
            while (!valid && attempts < 20) {
                obs = {
                    x: padding + Math.random() * (w - 2 * padding),
                    y: padding + Math.random() * (h - 2 * padding),
                    r: 25 + Math.random() * 20
                };

                // Check distance from start/end
                const dStart = Math.hypot(obs.x - this.startPoint.x, obs.y - this.startPoint.y);
                const dEnd = Math.hypot(obs.x - this.endPoint.x, obs.y - this.endPoint.y);

                if (dStart > safeDist + obs.r && dEnd > safeDist + obs.r) {
                    valid = true;
                }
                attempts++;
            }
            if (valid) this.obstacles.push(obs);
        }

        this.draw();
    }

    draw() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        // Draw Path
        this.ctx.beginPath();
        this.ctx.strokeStyle = this.primaryColor;
        this.ctx.lineWidth = 5;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
        if (this.path.length > 0) {
            this.ctx.moveTo(this.path[0].x, this.path[0].y);
            for (let i = 1; i < this.path.length; i++) {
                this.ctx.lineTo(this.path[i].x, this.path[i].y);
            }
        }
        this.ctx.stroke();

        // Draw Start
        this.ctx.beginPath();
        this.ctx.fillStyle = this.primaryColor;
        this.ctx.arc(this.startPoint.x, this.startPoint.y, this.startPoint.r, 0, Math.PI * 2);
        this.ctx.fill();

        // Label Start
        this.ctx.fillStyle = '#fff';
        this.ctx.font = '16px monospace';
        this.ctx.textAlign = 'center';
        this.ctx.fillText("START", this.startPoint.x, this.startPoint.y - 25);

        // Draw End
        this.ctx.beginPath();
        this.ctx.fillStyle = this.primaryColor;
        this.ctx.arc(this.endPoint.x, this.endPoint.y, this.endPoint.r, 0, Math.PI * 2);
        this.ctx.fill();

        // Label End
        this.ctx.fillStyle = '#fff';
        this.ctx.fillText("END", this.endPoint.x, this.endPoint.y - 25);

        // Draw Obstacles
        this.ctx.fillStyle = this.secondaryColor;
        this.obstacles.forEach(obs => {
            this.ctx.beginPath();
            this.ctx.arc(obs.x, obs.y, obs.r, 0, Math.PI * 2);
            this.ctx.fill();
        });
    }

    onDown(e) {
        const rect = this.canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Check if starting near start point
        const dist = Math.hypot(x - this.startPoint.x, y - this.startPoint.y);
        if (dist < this.startPoint.r * 2) {
            this.isDrawing = true;
            this.path = [{ x, y }];
        }
    }

    onMove(e) {
        if (!this.isDrawing || !this.active) return;

        const rect = this.canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        this.path.push({ x, y });

        // Check Obstacle Collision
        for (const obs of this.obstacles) {
            const dist = Math.hypot(x - obs.x, y - obs.y);
            if (dist < obs.r + 5) { // +5 for line width
                this.lose();
                this.isDrawing = false;
                return;
            }
        }

        // Check Win
        const distEnd = Math.hypot(x - this.endPoint.x, y - this.endPoint.y);
        if (distEnd < this.endPoint.r) {
            // Infinite Logic
            if (this.engine.activeChallenge && (this.engine.activeChallenge.mode === 'infinite' || (this.engine.activeChallenge.config && this.engine.activeChallenge.config.timeAttack))) {
                this.engine.score++;
                this.engine.updateHUD();
                this.isDrawing = false;
                this.startRound();
            } else {
                this.win();
                this.isDrawing = false;
            }
        }

        this.draw();
    }

    onUp() {
        this.isDrawing = false;
        // If released before end, clear path (optional, maybe just leave it)
        if (this.active) {
            this.path = [];
            this.draw();
        }
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
